import { useCallback, useEffect, useRef, useState } from 'react';
import { SessionService } from '../services/session.service';
import type {
  ClientMessage,
  MetricsTickPayload,
  RawEventPayload,
  ServerMessage,
  WsConnectionState
} from '../services/realtime.types';

/**
 * Cliente del WS `/ws/dashboard` de Raptor CPD (adaptado del pack `dashboard-frontend/useRealtimeWS.ts`).
 *
 * - Auth por `?token=` (el browser no permite headers en WebSocket).
 * - `hello` → backfill de eventos → `tick` cada ~1 s con el snapshot de métricas.
 * - Reconexión con backoff exponencial (1 s → 30 s). Close 4401 = token vencido: refresca vía
 *   SessionService (single-flight, comparte el refresh con el HTTP) y reconecta; 4403 = sin rol.
 * - Guarda un historial de ticks (ring buffer) para graficar series en el tiempo.
 */

export interface UseRealtimeWSOptions {
  url: string;
  /** cuántos ticks conservar para las series (1 tick ≈ 1 s) */
  maxTickHistory?: number;
  maxRecentEvents?: number;
  pingIntervalMs?: number;
  maxBackoffMs?: number;
  enabled?: boolean;
}

export interface UseRealtimeWSResult {
  state: WsConnectionState;
  hello?: { serverTime: string; tickMs: number; bufferedEvents: number };
  latestTick?: MetricsTickPayload;
  tickHistory: MetricsTickPayload[];
  recentEvents: RawEventPayload[];
  ticksReceived: number;
  lastCloseCode?: number;
  lastError?: { code: string; message: string };
  send: (msg: ClientMessage) => boolean;
  reconnect: () => void;
}

const getAccessToken = (): string | null => SessionService.getAccessToken();

export function useRealtimeWS(opts: UseRealtimeWSOptions): UseRealtimeWSResult {
  const {
    url,
    maxTickHistory = 300,
    maxRecentEvents = 100,
    pingIntervalMs = 20_000,
    maxBackoffMs = 30_000,
    enabled = true
  } = opts;

  const [state, setState] = useState<WsConnectionState>('idle');
  const [hello, setHello] = useState<UseRealtimeWSResult['hello']>();
  const [latestTick, setLatestTick] = useState<MetricsTickPayload>();
  const [tickHistory, setTickHistory] = useState<MetricsTickPayload[]>([]);
  const [recentEvents, setRecentEvents] = useState<RawEventPayload[]>([]);
  const [ticksReceived, setTicksReceived] = useState(0);
  const [lastCloseCode, setLastCloseCode] = useState<number>();
  const [lastError, setLastError] = useState<{ code: string; message: string }>();
  const [generation, setGeneration] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const backoffRef = useRef(1000);
  const disposedRef = useRef(false);
  const eventsRef = useRef<RawEventPayload[]>([]);
  const ticksRef = useRef<MetricsTickPayload[]>([]);

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    if (pingTimerRef.current) clearInterval(pingTimerRef.current);
    reconnectTimerRef.current = null;
    pingTimerRef.current = null;
  }, []);

  const send = useCallback((msg: ClientMessage): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(msg));
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    disposedRef.current = false;

    const scheduleReconnect = (delayMs?: number) => {
      if (disposedRef.current) return;
      const delay = delayMs ?? backoffRef.current;
      backoffRef.current = Math.min(backoffRef.current * 2, maxBackoffMs);
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    const connect = () => {
      if (disposedRef.current) return;
      const token = getAccessToken();
      if (!token) {
        setState('closed');
        return;
      }
      setState('connecting');
      const wsUrl = `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
      let ws: WebSocket;
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        setState('error');
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        setState('open');
        backoffRef.current = 1000;
        if (pingIntervalMs > 0) {
          pingTimerRef.current = setInterval(() => send({ type: 'ping' }), pingIntervalMs);
        }
      };

      ws.onmessage = (ev) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(String(ev.data)) as ServerMessage;
        } catch {
          return;
        }
        switch (msg.type) {
          case 'hello':
            setHello({
              serverTime: msg.serverTime,
              tickMs: msg.tickMs,
              bufferedEvents: msg.bufferedEvents
            });
            break;
          case 'tick': {
            setLatestTick(msg.payload);
            setTicksReceived((n) => n + 1);
            const next = [...ticksRef.current, msg.payload];
            if (next.length > maxTickHistory) next.splice(0, next.length - maxTickHistory);
            ticksRef.current = next;
            setTickHistory(next);
            break;
          }
          case 'event': {
            const next = [msg.payload, ...eventsRef.current];
            if (next.length > maxRecentEvents) next.length = maxRecentEvents;
            eventsRef.current = next;
            setRecentEvents(next);
            break;
          }
          case 'error':
            setLastError({ code: msg.code, message: msg.message });
            break;
          default:
            break;
        }
      };

      ws.onerror = () => setState('error');

      ws.onclose = async (ev) => {
        clearTimers();
        wsRef.current = null;
        setLastCloseCode(ev.code);
        setState('closed');
        if (disposedRef.current) return;
        if (ev.code === 4401) {
          const newToken = await SessionService.refreshAccessToken();
          if (newToken) scheduleReconnect(0);
          return;
        }
        if (ev.code === 4403) return; // sin rol: no insistir
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      disposedRef.current = true;
      clearTimers();
      const ws = wsRef.current;
      if (ws) ws.close(1000, 'unmount');
      wsRef.current = null;
    };
  }, [
    url,
    enabled,
    generation,
    maxTickHistory,
    maxRecentEvents,
    pingIntervalMs,
    maxBackoffMs,
    send,
    clearTimers
  ]);

  const reconnect = useCallback(() => {
    backoffRef.current = 1000;
    setGeneration((g) => g + 1);
  }, []);

  return {
    state,
    hello,
    latestTick,
    tickHistory,
    recentEvents,
    ticksReceived,
    lastCloseCode,
    lastError,
    send,
    reconnect
  };
}
